import type { ReactNode } from "react"
import { cn } from "@/shared/lib/utils"
import { SlidingPill } from "./sliding-pill"

export interface SegmentOption<T extends string | number> {
	value: T
	label: ReactNode
}

/**
 * Выбор одного из нескольких вариантов: серая дорожка и подложка, которая переезжает
 * к выбранному. Одинаковый во всей панели, от редактора поста до конструктора.
 * Всегда в одну строку: если места не хватает (телефон), дорожка прокручивается, как
 * вкладки раздела бота. Перенос ломал круглую дорожку на две неровные строки.
 */
export function SegmentedControl<T extends string | number>({
	options,
	value,
	onChange,
	allowEmpty = false,
	size = "default",
	className,
	"aria-label": ariaLabel,
}: {
	options: readonly SegmentOption<T>[]
	value: T | null | undefined
	onChange: (value: T | null) => void
	/** Повторный клик по выбранному снимает выбор. */
	allowEmpty?: boolean
	size?: "default" | "sm"
	className?: string
	"aria-label"?: string
}) {
	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className={cn("hide-scrollbar relative inline-flex max-w-full gap-1 overflow-x-auto rounded-full bg-secondary/60 p-1", className)}
		>
			<SlidingPill className="rounded-full" />
			{options.map((option) => {
				const active = option.value === value
				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={active}
						data-active={active}
						onClick={() => {
							if (active) {
								if (allowEmpty) onChange(null)
								return
							}
							onChange(option.value)
						}}
						className={cn(
							"relative shrink-0 whitespace-nowrap rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
							size === "sm" ? "min-w-8 px-2.5 py-1 text-xs" : "px-2.5 py-1.5 text-sm sm:px-3",
							active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
						)}
					>
						<span className="relative">{option.label}</span>
					</button>
				)
			})}
		</div>
	)
}
